import { useState, useEffect, useRef, useCallback } from 'react';

export const useAutoSave = (saveFunction, data, options = {}) => {
  const {
    delay = 30000, // 30 seconds default
    storageKey = 'autoSaveEnabled',
    enabled = true // External enable/disable control
  } = options;

  const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? JSON.parse(saved) : true; // Default to enabled
  });
  
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [nextSaveCountdown, setNextSaveCountdown] = useState(0);
  
  const countdownIntervalRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const lastDataRef = useRef(null);
  const dataChangedRef = useRef(false);
  const continuousAutoSaveRef = useRef(false);

  // Save auto-save enabled state to localStorage
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(isAutoSaveEnabled));
  }, [isAutoSaveEnabled, storageKey]);

  const clearAllTimers = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const startCountdown = () => {
    console.log('Auto-save: Starting countdown');
    setNextSaveCountdown(30);
    
    countdownIntervalRef.current = setInterval(() => {
      setNextSaveCountdown(prev => {
        const newValue = prev - 1;
        console.log('Auto-save: Countdown:', newValue);
        
        if (newValue <= 0) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          return 0;
        }
        return newValue;
      });
    }, 1000);
  };

  const performSave = async () => {
    if (!saveFunction) {
      console.log('Auto-save: No save function provided');
      return;
    }

    // Always attempt to save if auto-save is running
    console.log('🔄 Auto-save: PERFORMING SAVE, changes detected:', dataChangedRef.current);
    console.log('🔄 Auto-save: Save function type:', typeof saveFunction);
    setAutoSaveStatus('saving');

    try {
      await new Promise((resolve, reject) => {
        console.log('🔄 Auto-save: Calling save function...');
        saveFunction(
          null,
          (successMessage) => {
            console.log('✅ Auto-save: SUCCESS callback received:', successMessage);
            setAutoSaveStatus('success');
            setLastSaveTime(new Date());
            dataChangedRef.current = false;
            resolve(successMessage);
            
            setTimeout(() => setAutoSaveStatus('idle'), 2000);
          },
          (errorMessage) => {
            console.log('❌ Auto-save: ERROR callback received:', errorMessage);
            setAutoSaveStatus('error');
            reject(new Error(errorMessage));
            
            setTimeout(() => setAutoSaveStatus('idle'), 3000);
          }
        );
      });
    } catch (error) {
      console.error('❌ Auto-save failed with exception:', error);
      setAutoSaveStatus('error');
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
    }
  };

  const startAutoSave = useCallback(() => {
    if (!isAutoSaveEnabled || !enabled) {
      console.log('Auto-save: Not starting - disabled by user:', !isAutoSaveEnabled, 'or system:', !enabled);
      continuousAutoSaveRef.current = false;
      return;
    }

    console.log('Auto-save: Starting auto-save cycle');
    continuousAutoSaveRef.current = true;
    clearAllTimers();
    
    // Start countdown
    startCountdown();
    
    // Schedule save
    saveTimeoutRef.current = setTimeout(async () => {
      await performSave();
      saveTimeoutRef.current = null;
      
      // Always restart auto-save if it's enabled (continuous mode)
      setTimeout(() => {
        if (isAutoSaveEnabled && continuousAutoSaveRef.current) {
          console.log('Auto-save: Restarting continuous auto-save cycle');
          startAutoSave();
        }
      }, 1000);
    }, delay);
  }, [isAutoSaveEnabled, delay, enabled]);

  // Detect data changes and start auto-save if not already running
  useEffect(() => {
    const currentDataString = JSON.stringify(data);
    if (lastDataRef.current && currentDataString !== lastDataRef.current) {
      console.log('Auto-save: Data changed detected');
      console.log('Auto-save: Current state - enabled:', isAutoSaveEnabled, 'system enabled:', enabled, 'timeout exists:', !!saveTimeoutRef.current);
      dataChangedRef.current = true;
      
      // Start auto-save if enabled and not already running
      if (isAutoSaveEnabled && enabled && !saveTimeoutRef.current) {
        console.log('🚀 Auto-save: Starting new cycle due to data change');
        startAutoSave();
      } else if (isAutoSaveEnabled && enabled && saveTimeoutRef.current) {
        console.log('⏳ Auto-save: Cycle already running, will save when timer expires');
      } else {
        console.log('❌ Auto-save: Not starting - enabled:', isAutoSaveEnabled, 'system enabled:', enabled, 'timeout exists:', !!saveTimeoutRef.current);
      }
    }
    lastDataRef.current = currentDataString;
  }, [data, isAutoSaveEnabled, enabled, startAutoSave]);

  // Clear timers when disabled
  useEffect(() => {
    console.log('🔄 useAutoSave enabled state changed:', {
      isAutoSaveEnabled,
      enabled,
      shouldWork: isAutoSaveEnabled && enabled
    });
    
    if (!isAutoSaveEnabled || !enabled) {
      console.log('🛑 Clearing auto-save timers - disabled by user:', !isAutoSaveEnabled, 'or system:', !enabled);
      clearAllTimers();
      setNextSaveCountdown(0);
      continuousAutoSaveRef.current = false;
    } else {
      console.log('🚀 Auto-save is now enabled! Should start on next data change...');
      
      // If auto-save just got enabled, trigger it to start immediately
      if (isAutoSaveEnabled && enabled && !saveTimeoutRef.current) {
        console.log('🎯 Auto-save just got enabled - starting immediately');
        setTimeout(() => {
          dataChangedRef.current = true; // Force a save cycle
          startAutoSave();
        }, 100);
      }
    }
  }, [isAutoSaveEnabled, enabled, startAutoSave]);

  const saveNow = useCallback(async () => {
    console.log('Auto-save: Manual save triggered');
    clearAllTimers();
    setNextSaveCountdown(0);
    continuousAutoSaveRef.current = false;
    
    // Force save even if no changes detected
    dataChangedRef.current = true;
    await performSave();
    
    // Restart continuous auto-save if enabled
    if (isAutoSaveEnabled) {
      console.log('Auto-save: Restarting continuous cycle after manual save');
      setTimeout(() => {
        dataChangedRef.current = false;
        lastDataRef.current = JSON.stringify(data);
        startAutoSave();
      }, 1000);
    }
  }, [isAutoSaveEnabled, data, startAutoSave]);

  const toggleAutoSave = useCallback(() => {
    console.log('Auto-save: Toggling from', isAutoSaveEnabled);
    setIsAutoSaveEnabled(prev => {
      const newValue = !prev;
      if (!newValue || !enabled) {
        clearAllTimers();
        setNextSaveCountdown(0);
        continuousAutoSaveRef.current = false;
      } else if (enabled) {
        // When enabling auto-save, start continuous mode (only if system enabled)
        console.log('Auto-save: Enabled, starting continuous mode...');
        setTimeout(() => {
          const currentDataString = JSON.stringify(data);
          lastDataRef.current = currentDataString;
          dataChangedRef.current = true; // Force at least one save cycle
          startAutoSave();
        }, 100);
      }
      return newValue;
    });
  }, [isAutoSaveEnabled, enabled, data, startAutoSave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
      continuousAutoSaveRef.current = false;
    };
  }, []);

  return {
    isAutoSaveEnabled,
    autoSaveStatus,
    lastSaveTime,
    nextSaveCountdown,
    toggleAutoSave,
    saveNow,
    setIsAutoSaveEnabled
  };
}; 